/**
 * Store
 */
import { createMigrate, persistStore, persistReducer } from 'redux-persist';
import localForage from 'localforage';
import { Store, createStore, applyMiddleware, compose } from 'redux';
import createSagaMiddleware from 'redux-saga';
import { routerMiddleware } from 'connected-react-router';
import keyBy from 'lodash.keyby';
import * as Sentry from '@sentry/react';

// Reducers
// @ts-ignore
import { BrowserHistory } from 'history'; // eslint-disable-line
import createRootReducer from './reducers';

// Middleware
// @ts-ignore
import { keyByMiddleware } from './middleware/keyBy';
// @ts-ignore
import { logentries } from './middleware/logentries';
// @ts-ignore
import { normalizr } from './middleware/normalizr';
// @ts-ignore
import { googleTagManager } from './middleware/googleTagManager';

// Sagas
// @ts-ignore
import rootSaga from './sagas';

// Config
localForage.setDriver([localForage.WEBSQL, localForage.LOCALSTORAGE]);

export const migrations: any = {
    0: () => ({}),
    1: () => ({}),
    2: () => ({}),
    // TODO create a global state type that combines all the type definitions in the services
    3: (state: any) => ({
        auth: state.auth
    }),
    4: (state: any) => ({
        auth: state.auth
    }),
    5: (state: any) => ({
        ...state,
        harvestEvents: state.harvestEventsNew
    }),
    6: (state: any) => ({
        ...state,
        ui: {
            ...state.ui,
            timesheetGroupBy: null
        },
        // Override legacy harvestEvents
        harvestEvents: {
            data: {},
            updates: {},
            deletes: [],
            errors: []
        },
        // Trigger a reload of all data stored offline
        initialDataLoaded: {},
        hashes: {}
    }),
    7: (state: any) => ({
        ...state,
        // Override legacy entities
        entities: {
            data: {},
            updates: {},
            deletes: [],
            errors: []
        },
        // Trigger a reload of all data stored offline
        initialDataLoaded: {},
        hashes: {}
    }),
    8: ({ equipmentSets, ...state }: any) => ({
        ...state,
        equipmentSets: keyBy(equipmentSets, 'id')
    })
};

export const reduxPersistWhitelist: string[] = [
    'activeIngredients',
    'auth',
    'columnWidths',
    'initialDataLoaded',
    'organizations',
    'hashes',
    'hideUnusedGroups',
    'currentEntityId',
    'currentNumberOfDays',
    'currentOrganizationId',
    'currentSeasonId',
    'dates',
    'equipmentSets',
    'finalFormsUnsavedValues',
    'harvestEvents',
    'quantityForecasts',
    'usersMe',
    'myTimesheetEntries',
    'myTimesheets',
    // scaffoldData start
    'commodities',
    'descriptionSnippets',
    'entities',
    'entityTypes',
    'groupEntities',
    'groupPeople',
    'groups',
    'harvestPrintProperties',
    'organizationConfig',
    'people',
    'pickTypes',
    'roles',
    'schemaAttributes',
    'schemas',
    'seasons',
    'userEntityAssignments',
    'varieties',
    'workTypes',
    'products',
    'productTypes',
    'unitTypes',
    'equipment',
    'timesheetHolidays',
    'payrollExports',
    // scaffoldData end
    'ui',
    'onlyMyTimesheetsCheckbox',
    'waterSource',
    'spraySubmissions',
    'sprayEvents',
    'fertilizerProducts'
];

const config: any = {
    version: '7',
    key: 'primary',
    storage: localForage,
    debug: process.env.NODE_ENV !== 'production',
    whitelist: reduxPersistWhitelist,
    migrate: createMigrate(migrations, { debug: false })
};

const persistedReducer = (history: BrowserHistory): any =>
    persistReducer(config, createRootReducer(history));

const sagaMiddleware = createSagaMiddleware();

export default function configureStore(history: BrowserHistory): any {
    // Create the store with two middlewares
    // 1. sagaMiddleware: Makes redux-sagas work
    // 2. routerMiddleware: Syncs the location/URL path to the state
    const middlewares: any = [
        sagaMiddleware,
        routerMiddleware(history),
        googleTagManager,
        logentries,
        keyByMiddleware,
        normalizr
    ];

    // Create Sentry Redux enhancer
    const sentryReduxEnhancer = Sentry.createReduxEnhancer({
        stateTransformer: (state) => {
            const sanitized = { ...state };

            // Remove sensitive data
            if (sanitized.auth) {
                sanitized.auth = {
                    ...sanitized.auth,
                    idToken: '[REDACTED]',
                    accessToken: '[REDACTED]'
                };
            }

            // Remove form data which may contain passwords
            delete sanitized.form;

            return sanitized;
        },
        actionTransformer: (action) => {
            // Don't log redux-form actions
            if (action.type.startsWith('@@redux-form/')) {
                return null;
            }
            return action;
        }
    });

    const enhancers: any = [
        applyMiddleware(...middlewares),
        sentryReduxEnhancer
    ];

    // If Redux DevTools Extension is installed use it, otherwise use Redux compose
    const composeEnhancers =
        // @ts-ignore
        window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

    const store: Store<any, any> = createStore(
        persistedReducer(history),
        composeEnhancers(...enhancers)
    );

    sagaMiddleware.run(rootSaga);

    const persistor = persistStore(store);

    return { persistor, store };
}
